import{reportEarnings}from'./market-api.js';

const SYNC_INTERVAL_MS=60_000;
const QUEUE_VERSION=1;
let userId='';
let queue=[];
let pending=null;
let timer=null;
let flushing=null;
let wallet=null;
let transfers=[];
let hooks={getBalance:()=>0,setBalance:()=>{},getTrainerLevel:()=>1,getRouteLevel:()=>1,onWallet:()=>{}};

const storageKey=()=>`idlemon-wallet-sync:v${QUEUE_VERSION}:${userId}`;
const validEvent=event=>event&&typeof event==='object'&&typeof event.id==='string'&&['migration','earn','spend'].includes(event.kind);

function persist(){
  if(!userId)return;
  localStorage.setItem(storageKey(),JSON.stringify({version:QUEUE_VERSION,queue,pending,transfers}));
}

function restore(){
  try{
    const saved=JSON.parse(localStorage.getItem(storageKey())||'{}');
    queue=Array.isArray(saved.queue)?saved.queue.filter(validEvent):[];
    pending=saved.pending&&Array.isArray(saved.pending.events)&&saved.pending.events.every(validEvent)?saved.pending:null;
    transfers=Array.isArray(saved.transfers)?saved.transfers.filter(entry=>entry?.requestId&&entry?.action&&entry?.args):[];
  }catch{queue=[];pending=null;transfers=[]}
}

function errorCode(error){return String(error?.code||error?.message||error||'').toLowerCase()}

function applyWallet(next){
  if(!next)return;
  wallet={...wallet,...next,idlecoin:Number(next.idlecoin)||0,emerald:Number(next.emerald)||0};
  hooks.setBalance(wallet.idlecoin);
  hooks.onWallet(structuredClone(wallet));
}

function migrationEvent(){
  return{id:crypto.randomUUID(),kind:'migration',balance:Math.max(0,Math.floor(Number(hooks.getBalance())||0))};
}

function nextBatch(){
  if(pending)return pending;
  if(!wallet?.migration_completed_at){
    const existing=queue.find(event=>event.kind==='migration');
    const event=existing||migrationEvent();
    queue=queue.filter(item=>item!==existing&&item.kind!=='migration');
    pending={requestId:crypto.randomUUID(),events:[event]};
  }else if(queue.length){
    pending={requestId:crypto.randomUUID(),events:queue.splice(0,200)};
  }
  persist();
  return pending;
}

/** Fontes debitadas/creditadas por RPC dedicada — não entram no queue de report_earnings. */
const SERVER_LEDGER_SOURCES=new Set([
  'shop_purchase','shop_buy','item_sale','pokemon_sale','breeding','evolution','respec','spend_misc',
]);

export function walletSyncEnabled(){
  return!!userId&&!!wallet?.migration_completed_at;
}

export function recordIdlecoinDelta(delta,source){
  const amount=Math.abs(Math.trunc(Number(delta)||0));
  if(!userId||!amount)return;
  if(walletSyncEnabled()&&SERVER_LEDGER_SOURCES.has(String(source||'')))return;
  queue.push({
    id:crypto.randomUUID(),
    kind:delta>0?'earn':'spend',
    amount,
    source:String(source||'gameplay').slice(0,64),
    trainer_level:Math.max(1,Math.min(300,Math.trunc(Number(hooks.getTrainerLevel())||1))),
    route_level:Math.max(1,Math.min(300,Math.trunc(Number(hooks.getRouteLevel())||1))),
    occurred_at:new Date().toISOString(),
  });
  persist();
}

export async function flushWalletSync(){
  if(flushing||!userId)return flushing;
  const batch=nextBatch();
  if(!batch)return wallet;
  flushing=(async()=>{
    try{
      const response=await reportEarnings(batch.events,batch.requestId);
      pending=null;
      if(response?.migration_completed)wallet.migration_completed_at=new Date().toISOString();
      applyWallet(response?.wallet);
      persist();
      if(queue.length)queueMicrotask(()=>flushWalletSync());
      return response;
    }catch(error){
      const code=errorCode(error);
      if(code.includes('migration_amnesty_pending'))return{migrationPending:true,wallet};
      console.warn('wallet sync failed',error);
      throw error;
    }finally{flushing=null}
  })();
  return flushing;
}

export async function initMarketState({account,getBalance,setBalance,getTrainerLevel,getRouteLevel,onWallet}={}){
  userId=account?.session?.user?.id||'';
  if(!userId)return null;
  wallet={...(account.wallet||{}),idlecoin:Number(account.wallet?.idlecoin)||0,emerald:Number(account.wallet?.emerald)||0};
  hooks={
    getBalance:getBalance||hooks.getBalance,
    setBalance:setBalance||hooks.setBalance,
    getTrainerLevel:getTrainerLevel||hooks.getTrainerLevel,
    getRouteLevel:getRouteLevel||hooks.getRouteLevel,
    onWallet:onWallet||hooks.onWallet,
  };
  restore();
  if(wallet.migration_completed_at){
    if(pending?.events?.some(event=>event.kind==='migration'))pending=null;
    queue=queue.filter(event=>event.kind!=='migration');
    persist();
  }
  if(wallet.migration_completed_at&&!pending&&!queue.length)applyWallet(wallet);
  clearInterval(timer);
  timer=setInterval(()=>flushWalletSync().catch(()=>{}),SYNC_INTERVAL_MS);
  addEventListener('visibilitychange',()=>{if(document.visibilityState==='hidden')flushWalletSync().catch(()=>{})});
  addEventListener('pagehide',()=>flushWalletSync().catch(()=>{}),{once:true});
  const result=await flushWalletSync().catch(error=>({error}));
  return{wallet:structuredClone(wallet),migrationPending:!!result?.migrationPending};
}

export function currentMarketWallet(){return wallet?structuredClone(wallet):null}

export function acceptMarketWallet(next){applyWallet(next);return currentMarketWallet()}

export async function runMarketTransfer({action,args,remote,applyLocal,requestId=crypto.randomUUID()}){
  let entry=transfers.find(item=>item.requestId===requestId);
  if(!entry){
    entry={requestId,action,args,response:null,createdAt:new Date().toISOString()};
    transfers.push(entry);persist();
  }
  if(!entry.response){entry.response=await remote(entry.requestId,entry.args);persist()}
  await applyLocal(entry.response,entry.requestId,entry.args);
  transfers=transfers.filter(item=>item.requestId!==entry.requestId);persist();
  return entry.response;
}

export async function resumeMarketTransfers(handlers={}){
  for(const entry of [...transfers]){
    const handler=handlers[entry.action];if(!handler)continue;
    await runMarketTransfer({
      ...handler,args:entry.args,requestId:entry.requestId,
      remote:handler.remote,applyLocal:handler.applyLocal,
    });
  }
  return transfers.length;
}
